import { IPaginator } from 'models/paginator';

export interface IApiKey {
  uniqueId: string;
  username: string;
  type: string;
  typeDictionaryUniqueId: string;
  key: string;
  status: string;
  statusDictionaryUniqueId: string;
  lastUsedDate: string;
  expiryDate: string;
  expiryStatus: string;
  expiryStatusDictionaryUniqueId: string;
}

export interface IApiKeysResponse extends IPaginator {
  content: IApiKey[];
}

export interface IApiKeyResponse {
  type: string;
  username: string;
  expiryDate: string | null;
  description: string;
  contactEmail: string;
  firstName: string;
  lastName: string;
  phonePrefixDictionaryUniqueId: string;
  phoneNumber: string;
  phonePrefixValue: { languageUniqueId: string; name: string; uniqueId: string }[];
  statusDictionaryUniqueId: string;
  lastUsedDate: string | null;
  uniqueId: string;

  key: string;

  apikeyAncillaryAccessory: { [key: string]: boolean };
  apikeyAncillaryAmmunition: { [key: string]: boolean };
  apikeyAncillaryComponent: { [key: string]: boolean };
  apikeyFirearm: { [key: string]: boolean };
  apikeyLegalEntity: { [key: string]: boolean };
  apikeyTransaction: { [key: string]: boolean };
  apikeyWeaponLicence: { [key: string]: boolean };
}

export interface IApiKeyRequest {
  apikeyAncillaryAccessory: { [key: string]: boolean };
  apikeyAncillaryAmmunition: { [key: string]: boolean };
  apikeyAncillaryComponent: { [key: string]: boolean };
  apikeyFirearm: { [key: string]: boolean };
  apikeyLegalEntity: { [key: string]: boolean };
  apikeyTransaction: { [key: string]: boolean };
  apikeyWeaponLicence: { [key: string]: boolean };

  type: string;
  username: string;
  expiryDate: string | null;
  description: string;
  contactEmail: string;
  firstName: string;
  lastName: string;
  contactPhone: {
    codeDictionaryUniqueId: string;
    value: string;
  };

  [key: string]: any;
}

export interface IApiKeyHistory {
  uniqueId: string;
  eventTypeUniqueId: string;
  eventType: string;
  eventNameUniqueId: string;
  eventName: string;
  eventDate: string;
  authorUniqueId: string;
  authorFirstName: string;
  authorLastName: string;
  authorRoles: string[];
}

export interface IApiKeyHistoryResponse extends Array<IApiKeyHistory> {}

export const apiKeyScopeList = [
  {
    label: 'apiKeysForm.scope.legalEntities.label',
    value: 'apikeyLegalEntity',
    licenceModule: false,
    items: [
      {
        label: 'apiKeysForm.scope.legalEntities.id.label',
        value: 'apikeyLegalEntity_leId',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.displayId.label',
        value: 'apikeyLegalEntity_leDisplayId',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.type.label',
        value: 'apikeyLegalEntity_leType',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.group.label',
        value: 'apikeyLegalEntity_leGroup',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.address.label',
        value: 'apikeyLegalEntity_leAddress',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.contacts.label',
        value: 'apikeyLegalEntity_leContacts',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.customFields.label',
        value: 'apikeyLegalEntity_leCustomFields',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.primaryLicencingAuthority.label',
        value: 'apikeyLegalEntity_lePrimaryLicencingAuthority',
        licenceModule: true,
      },
      {
        label: 'apiKeysForm.scope.legalEntities.secondaryLicencingAuthority.label',
        value: 'apikeyLegalEntity_leSecondaryLicencingAuthority',
        licenceModule: true,
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualPrefix.label',
        value: 'apikeyLegalEntity_leIndividualPrefix',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualName.label',
        value: 'apikeyLegalEntity_leIndividualName',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualSurname.label',
        value: 'apikeyLegalEntity_leIndividualSurname',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualDateOfBirth.label',
        value: 'apikeyLegalEntity_leIndividualDateOfBirth',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualGender.label',
        value: 'apikeyLegalEntity_leIndividualGender',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualMiddleNames.label',
        value: 'apikeyLegalEntity_leIndividualMiddleNames',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualIdentityDocumentType.label',
        value: 'apikeyLegalEntity_leIndividualIdentityDocumentType',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualIdentityDocumentNumber.label',
        value: 'apikeyLegalEntity_leIndividualIdentityDocumentNumber',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualPlaceOfBirth.label',
        value: 'apikeyLegalEntity_leIndividualPlaceOfBirth',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualNationality.label',
        value: 'apikeyLegalEntity_leIndividualNationality',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualHeight.label',
        value: 'apikeyLegalEntity_leIndividualHeight',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.individualOccupation.label',
        value: 'apikeyLegalEntity_leIndividualOccupation',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.organisationName.label',
        value: 'apikeyLegalEntity_leOrganisationName',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.organisationCategory.label',
        value: 'apikeyLegalEntity_leOrganisationCategory',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.organisationDepartmentName.label',
        value: 'apikeyLegalEntity_leOrganisationDepartmentName',
      },
      {
        label: 'apiKeysForm.scope.legalEntities.organisationType.label',
        value: 'apikeyLegalEntity_leOrganisationTypes',
      },
      {
        label: 'apiKeyForm.scope.legalEntities.status.label',
        value: 'apikeyLegalEntity_leStatus',
      },
      {
        label: 'apiKeyForm.scope.legalEntities.deactivationReason.label',
        value: 'apikeyLegalEntity_leDeactivationReason',
      },
    ],
  },
  {
    label: 'apiKeysForm.scope.firearms.label',
    value: 'apikeyFirearm',
    licenceModule: false,
    items: [
      {
        label: 'apiKeysForm.scope.firearms.fireCycleId.label',
        value: 'apikeyFirearm_fireCycleId',
      },
      {
        label: 'apiKeysForm.scope.firearms.serialNumber.label',
        value: 'apikeyFirearm_serialNumber',
      },
      {
        label: 'apiKeysForm.scope.firearms.exhibitNumber.label',
        value: 'apikeyFirearm_exhibitNumber',
      },
      {
        label: 'apiKeysForm.scope.firearms.appliedMarking.label',
        value: 'apikeyFirearm_appliedMarking',
      },
      {
        label: 'apiKeysForm.scope.firearms.importMarking.label',
        value: 'apikeyFirearm_importMarking',
      },
      {
        label: 'apiKeysForm.scope.firearms.exportMarking.label',
        value: 'apikeyFirearm_exportMarking',
      },
      {
        label: 'apiKeysForm.scope.firearms.deactivationMarking.label',
        value: 'apikeyFirearm_deactivationMarking',
      },
      {
        label: 'apiKeysForm.scope.firearms.confiscationMarking.label',
        value: 'apikeyFirearm_confiscationMarking',
      },
      {
        label: 'apiKeysForm.scope.firearms.surplusSaleMarking.label',
        value: 'apikeyFirearm_surplusSaleMarking',
      },
      {
        label: 'apiKeysForm.scope.firearms.manufacturer.label',
        value: 'apikeyFirearm_manufacturer',
      },
      { label: 'apiKeysForm.scope.firearms.make.label', value: 'apikeyFirearm_make' },
      { label: 'apiKeysForm.scope.firearms.model.label', value: 'apikeyFirearm_model' },
      { label: 'apiKeysForm.scope.firearms.type.label', value: 'apikeyFirearm_type' },
      { label: 'apiKeysForm.scope.firearms.calibre.label', value: 'apikeyFirearm_calibre' },
      { label: 'apiKeysForm.scope.firearms.action.label', value: 'apikeyFirearm_action' },
      {
        label: 'apiKeysForm.scope.firearms.dateOfManufacture.label',
        value: 'apikeyFirearm_dateOfManufacture',
      },
      {
        label: 'apiKeysForm.scope.firearms.countryOfManufacture.label',
        value: 'apikeyFirearm_countryOfManufacture',
      },
      {
        label: 'apiKeysForm.scope.firearms.countryOfImport.label',
        value: 'apikeyFirearm_countryOfImport',
      },
      { label: 'apiKeysForm.scope.firearms.owner.label', value: 'apikeyFirearm_owner' },
      { label: 'apiKeysForm.scope.firearms.keeper.label', value: 'apikeyFirearm_keeper' },
      { label: 'apiKeysForm.scope.firearms.state.label', value: 'apikeyFirearm_state' },
      { label: 'apiKeysForm.scope.firearms.legality.label', value: 'apikeyFirearm_legality' },

      { label: 'apiKeysForm.scope.firearms.id.label', value: 'apikeyFirearm_firearmId' },
      { label: 'apiKeysForm.scope.firearms.keeper.id.label', value: 'apikeyFirearm_keeperId' },
      { label: 'apiKeysForm.scope.firearms.owner.id.label', value: 'apikeyFirearm_ownerId' },
      {
        label: 'apiKeyForm.scope.firearm.yearOfConfiscation.label',
        value: 'apikeyFirearm_yearOfConfiscation',
      },
      {
        label: 'apiKeyForm.scope.firearm.yearOfTransfer.label',
        value: 'apikeyFirearm_yearOfTransfer',
      },
      {
        label: 'apiKeysForm.scope.firearms.customFields.label',
        value: 'apikeyFirearm_customFields',
      },
    ],
  },
  {
    label: 'apiKeysForm.scope.accessories.label',
    value: 'apikeyAncillaryAccessory',
    licenceModule: false,
    items: [
      {
        label: 'apiKeysForm.scope.accessories.fireCycleId.label',
        value: 'apikeyAncillaryAccessory_fireCycleId',
      },
      {
        label: 'apiKeysForm.scope.accessories.serialNumber.label',
        value: 'apikeyAncillaryAccessory_serialNumber',
      },
      {
        label: 'apiKeysForm.scope.accessories.exhibitNumber.label',
        value: 'apikeyAncillaryAccessory_exhibitNumber',
      },
      {
        label: 'apiKeysForm.scope.accessories.accessoryType.label',
        value: 'apikeyAncillaryAccessory_accessoryType',
      },
      {
        label: 'apiKeysForm.scope.accessories.accessoryManufacturer.label',
        value: 'apikeyAncillaryAccessory_accessoryManufacturer',
      },
      {
        label: 'apiKeysForm.scope.accessories.hostType.label',
        value: 'apikeyAncillaryAccessory_hostType',
      },
      {
        label: 'apiKeysForm.scope.accessories.owner.label',
        value: 'apikeyAncillaryAccessory_owner',
      },
      {
        label: 'apiKeysForm.scope.accessories.keeper.label',
        value: 'apikeyAncillaryAccessory_keeper',
      },
      {
        label: 'apiKeysForm.scope.accessories.state.label',
        value: 'apikeyAncillaryAccessory_state',
      },
      {
        label: 'apiKeysForm.scope.accessories.legality.label',
        value: 'apikeyAncillaryAccessory_legality',
      },
      {
        label: 'apiKeysForm.scope.accessories.id.label',
        value: 'apikeyAncillaryAccessory_accessoryId',
      },
      {
        label: 'apiKeysForm.scope.accessories.keeper.id.label',
        value: 'apikeyAncillaryAccessory_keeperId',
      },
      {
        label: 'apiKeysForm.scope.accessories.owner.id.label',
        value: 'apikeyAncillaryAccessory_ownerId',
      },
      {
        label: 'apiKeysForm.scope.accessories.appliedMarking.label',
        value: 'apikeyAncillaryAccessory_appliedMarking',
      },
      {
        label: 'apiKeysForm.scope.accessories.importMarking.label',
        value: 'apikeyAncillaryAccessory_importMarking',
      },
      {
        label: 'apiKeysForm.scope.accessories.exportMarking.label',
        value: 'apikeyAncillaryAccessory_exportMarking',
      },
      {
        label: 'apiKeysForm.scope.accessories.deactivationMarking.label',
        value: 'apikeyAncillaryAccessory_deactivationMarking',
      },
      {
        label: 'apiKeysForm.scope.accessories.confiscationMarking.label',
        value: 'apikeyAncillaryAccessory_confiscationMarking',
      },
      {
        label: 'apiKeysForm.scope.accessories.surplusSaleMarking.label',
        value: 'apikeyAncillaryAccessory_surplusSaleMarking',
      },
      {
        label: 'apiKeyForm.scope.accessories.yearOfConfiscation.label',
        value: 'apikeyAncillaryAccessory_yearOfConfiscation',
      },
      {
        label: 'apiKeyForm.scope.accessories.yearOfTransfer.label',
        value: 'apikeyAncillaryAccessory_yearOfTransfer',
      },
    ],
  },
  {
    label: 'apiKeysForm.scope.components.label',
    value: 'apikeyAncillaryComponent',
    licenceModule: false,
    items: [
      {
        label: 'apiKeysForm.scope.components.fireCycleId.label',
        value: 'apikeyAncillaryComponent_fireCycleId',
      },
      {
        label: 'apiKeysForm.scope.components.serialNumber.label',
        value: 'apikeyAncillaryComponent_serialNumber',
      },
      {
        label: 'apiKeysForm.scope.components.exhibitNumber.label',
        value: 'apikeyAncillaryComponent_exhibitNumber',
      },
      {
        label: 'apiKeysForm.scope.components.type.label',
        value: 'apikeyAncillaryComponent_componentType',
      },
      {
        label: 'apiKeysForm.scope.components.manufacturer.label',
        value: 'apikeyAncillaryComponent_componentManufacturer',
      },
      {
        label: 'apiKeysForm.scope.components.firearmType.label',
        value: 'apikeyAncillaryComponent_firearmType',
      },
      {
        label: 'apiKeysForm.scope.components.firearmMake.label',
        value: 'apikeyAncillaryComponent_firearmMake',
      },
      {
        label: 'apiKeysForm.scope.components.firearmModel.label',
        value: 'apikeyAncillaryComponent_firearmModel',
      },
      {
        label: 'apiKeysForm.scope.components.firearmManufacturer.label',
        value: 'apikeyAncillaryComponent_firearmManufacturer',
      },
      {
        label: 'apiKeysForm.scope.components.owner.label',
        value: 'apikeyAncillaryComponent_owner',
      },
      {
        label: 'apiKeysForm.scope.components.keeper.label',
        value: 'apikeyAncillaryComponent_keeper',
      },
      {
        label: 'apiKeysForm.scope.components.state.label',
        value: 'apikeyAncillaryComponent_state',
      },
      {
        label: 'apiKeysForm.scope.components.legality.label',
        value: 'apikeyAncillaryComponent_legality',
      },
      {
        label: 'apiKeysForm.scope.components.id.label',
        value: 'apikeyAncillaryComponent_componentId',
      },
      {
        label: 'apiKeysForm.scope.components.keeper.id.label',
        value: 'apikeyAncillaryComponent_keeperId',
      },
      {
        label: 'apiKeysForm.scope.components.owner.id.label',
        value: 'apikeyAncillaryComponent_ownerId',
      },
      {
        label: 'apiKeysForm.scope.components.appliedMarking.label',
        value: 'apikeyAncillaryComponent_appliedMarking',
      },
      {
        label: 'apiKeysForm.scope.components.importMarking.label',
        value: 'apikeyAncillaryComponent_importMarking',
      },
      {
        label: 'apiKeysForm.scope.components.exportMarking.label',
        value: 'apikeyAncillaryComponent_exportMarking',
      },
      {
        label: 'apiKeysForm.scope.components.deactivationMarking.label',
        value: 'apikeyAncillaryComponent_deactivationMarking',
      },
      {
        label: 'apiKeysForm.scope.components.confiscationMarking.label',
        value: 'apikeyAncillaryComponent_confiscationMarking',
      },
      {
        label: 'apiKeysForm.scope.components.surplusSaleMarking.label',
        value: 'apikeyAncillaryComponent_surplusSaleMarking',
      },
      {
        label: 'apiKeyForm.scope.components.yearOfConfiscation.label',
        value: 'apikeyAncillaryComponent_yearOfConfiscation',
      },
      {
        label: 'apiKeyForm.scope.components.yearOfTransfer.label',
        value: 'apikeyAncillaryComponent_yearOfTransfer',
      },
    ],
  },
  {
    label: 'apiKeysForm.scope.ammunition.label',
    value: 'apikeyAncillaryAmmunition',
    licenceModule: false,
    items: [
      {
        label: 'apiKeysForm.scope.ammunition.fireCycleId.label',
        value: 'apikeyAncillaryAmmunition_fireCycleId',
      },
      {
        label: 'apiKeysForm.scope.ammunition.lotNumber.label',
        value: 'apikeyAncillaryAmmunition_lotNumber',
      },
      {
        label: 'apiKeysForm.scope.ammunition.batchNumber.label',
        value: 'apikeyAncillaryAmmunition_batchNumber',
      },
      {
        label: 'apiKeysForm.scope.ammunition.exhibitNumber.label',
        value: 'apikeyAncillaryAmmunition_exhibitNumber',
      },
      {
        label: 'apiKeysForm.scope.ammunition.ammunitionType.label',
        value: 'apikeyAncillaryAmmunition_ammunitionType',
      },
      {
        label: 'apiKeysForm.scope.ammunition.ammunitionManufacturer.label',
        value: 'apikeyAncillaryAmmunition_ammunitionManufacturer',
      },
      {
        label: 'apiKeysForm.scope.ammunition.functionalType.label',
        value: 'apikeyAncillaryAmmunition_functionalType',
      },
      {
        label: 'apiKeysForm.scope.ammunition.calibre.label',
        value: 'apikeyAncillaryAmmunition_calibre',
      },
      {
        label: 'apiKeysForm.scope.ammunition.headstampYear.label',
        value: 'apikeyAncillaryAmmunition_headstampYear',
      },
      {
        label: 'apiKeysForm.scope.ammunition.alphanumericMarkings.label',
        value: 'apikeyAncillaryAmmunition_alphanumericMarking',
      },
      {
        label: 'apiKeysForm.scope.ammunition.dateOfManufacture.label',
        value: 'apikeyAncillaryAmmunition_dateOfManufacture',
      },
      {
        label: 'apiKeysForm.scope.ammunition.owner.label',
        value: 'apikeyAncillaryAmmunition_owner',
      },
      {
        label: 'apiKeysForm.scope.ammunition.keeper.label',
        value: 'apikeyAncillaryAmmunition_keeper',
      },
      {
        label: 'apiKeysForm.scope.ammunition.state.label',
        value: 'apikeyAncillaryAmmunition_state',
      },
      {
        label: 'apiKeysForm.scope.ammunition.legality.label',
        value: 'apikeyAncillaryAmmunition_legality',
      },
      {
        label: 'apiKeysForm.scope.ammunition.id.label',
        value: 'apikeyAncillaryAmmunition_ammunitionId',
      },
      {
        label: 'apiKeysForm.scope.ammunition.keeper.id.label',
        value: 'apikeyAncillaryAmmunition_keeperId',
      },
      {
        label: 'apiKeysForm.scope.ammunition.owner.id.label',
        value: 'apikeyAncillaryAmmunition_ownerId',
      },
      {
        label: 'apiKeysForm.scope.ammunition.appliedMarking.label',
        value: 'apikeyAncillaryAmmunition_appliedMarking',
      },
      {
        label: 'apiKeysForm.scope.ammunition.importMarking.label',
        value: 'apikeyAncillaryAmmunition_importMarking',
      },
      {
        label: 'apiKeysForm.scope.ammunition.exportMarking.label',
        value: 'apikeyAncillaryAmmunition_exportMarking',
      },
      {
        label: 'apiKeysForm.scope.ammunition.deactivationMarking.label',
        value: 'apikeyAncillaryAmmunition_deactivationMarking',
      },
      {
        label: 'apiKeysForm.scope.ammunition.confiscationMarking.label',
        value: 'apikeyAncillaryAmmunition_confiscationMarking',
      },
      {
        label: 'apiKeysForm.scope.ammunition.surplusSaleMarking.label',
        value: 'apikeyAncillaryAmmunition_surplusSaleMarking',
      },
      {
        label: 'apiKeyForm.scope.ammunition.yearOfConfiscation.label',
        value: 'apikeyAncillaryAmmunition_yearOfConfiscation',
      },
      {
        label: 'apiKeyForm.scope.ammunition.yearOfTransfer.label',
        value: 'apikeyAncillaryAmmunition_yearOfTransfer',
      },
    ],
  },
  {
    label: 'apiKeysForm.scope.transactions.label',
    value: 'apikeyTransaction',
    licenceModule: false,
    items: [
      {
        label: 'apiKeysForm.scope.transactions.transactionType.label',
        value: 'apikeyTransaction_transactionType',
      },
      {
        label: 'apiKeysForm.scope.transactions.transactionDate.label',
        value: 'apikeyTransaction_transactionDate',
      },
      {
        label: 'apiKeysForm.scope.transactions.transactionCoordinates.label',
        value: 'apikeyTransaction_transactionCoordinates',
      },
      {
        label: 'apiKeysForm.scope.transactions.transactionId.label',
        value: 'apikeyTransaction_transactionId',
      },
      {
        label: 'apiKeysForm.scope.transactions.owner.label',
        value: 'apikeyTransaction_owner',
      },
      {
        label: 'apiKeysForm.scope.transactions.ownerId.label',
        value: 'apikeyTransaction_ownerId',
      },
      {
        label: 'apiKeysForm.scope.transactions.keeper.label',
        value: 'apikeyTransaction_keeper',
      },
      {
        label: 'apiKeysForm.scope.transactions.keeperId.label',
        value: 'apikeyTransaction_keeperId',
      },
      {
        label: 'apiKeysForm.scope.transactions.firearmIds.label',
        value: 'apikeyTransaction_firearmIds',
      },
      {
        label: 'apiKeysForm.scope.transactions.ancillaryIds.label',
        value: 'apikeyTransaction_ancillaryIds',
      },
      {
        label: 'apiKeysForm.scope.transactions.expirationDate.label',
        value: 'apikeyTransaction_expirationDate',
      },
      {
        label: 'apiKeysForm.scope.transactions.country.label',
        value: 'apikeyTransaction_country',
      },
      {
        label: 'apiKeysForm.scope.transactions.countryOfExport.label',
        value: 'apikeyTransaction_countryOfExport',
      },
      {
        label: 'apiKeysForm.scope.transactions.nameOfImporter.label',
        value: 'apikeyTransaction_nameOfImporter',
      },
      {
        label: 'apiKeyForm.scope.transactions.yearOfConfiscation.label',
        value: 'apikeyTransaction_yearOfConfiscation',
      },
      {
        label: 'apiKeyForm.scope.transactions.yearOfTransfer.label',
        value: 'apikeyTransaction_yearOfTransfer',
      },
    ],
  },
  {
    label: 'apiKeysForm.scope.weaponLicences.label',
    value: 'apikeyWeaponLicence',
    licenceModule: true,
    items: [
      {
        label: 'apiKeysForm.scope.weaponLicences.licenceId.label',
        value: 'apikeyWeaponLicence_licenceId',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.licenceNumber.label',
        value: 'apikeyWeaponLicence_licenceNumber',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.name.label',
        value: 'apikeyWeaponLicence_name',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.code.label',
        value: 'apikeyWeaponLicence_code',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.startDate.label',
        value: 'apikeyWeaponLicence_startDate',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.expiryDate.label',
        value: 'apikeyWeaponLicence_expiryDate',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.status.label',
        value: 'apikeyWeaponLicence_status',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.licenceAuthority.label',
        value: 'apikeyWeaponLicence_licenceAuthority',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.legalUniqueId.label',
        value: 'apikeyWeaponLicence_legalUniqueId',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.hasAnyFirearmIllegal.label',
        value: 'apikeyWeaponLicence_hasAnyFirearmIllegal',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.hasAnyFirearmNotAssignedToLegal.label',
        value: 'apikeyWeaponLicence_hasAnyFirearmNotAssignedToLegal',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.internalReason.label',
        value: 'apikeyWeaponLicence_internalReason',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.externalReason.label',
        value: 'apikeyWeaponLicence_externalReason',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.surrenderedDate.label',
        value: 'apikeyWeaponLicence_surrenderedDate',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.revokedDate.label',
        value: 'apikeyWeaponLicence_revokedDate',
      },
      {
        label: 'apiKeysForm.scope.weaponLicences.suspendedDate.label',
        value: 'apikeyWeaponLicence_suspendedDate',
      },
    ],
  },
];

export enum ApiKeyStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export enum ApiKeysStatusIds {
  ACTIVE = '11d0927c-0746-11ed-b939-0242ac120002',
  INACTIVE = '11d094fc-0746-11ed-b939-0242ac120002',
}
